import React, { useEffect, useState } from 'react';
import InputField from 'components/fields/InputField';

const AddQuestion = ({ setTestData, testData, setDisable }) => {
  const [errors, setErrors] = useState([]);
  const [completionTime, setCompletionTime] = useState('60 minutes'); //testData.completionTime ||
  const [completionTimeInMinutes, setCompletionTimeInMinutes] = useState(60);

  const convertToMinutes = (value, unit) => {
    const numValue = parseFloat(value);
    switch (unit) {
      case 'hour':
        return numValue * 60;
      case 'day':
        return numValue * 1440;
      case 'minute':
      default:
        return numValue;
    }
  };

  const handleCompletionTimeChange = (event) => {
    const { name, value } = event.target;

    setTestData((prevData) => {
      const [prevValue, prevUnit] = completionTime.split(' ');
      let updatedValue = prevValue;
      let updatedUnit = prevUnit || 'hour';

      // Update the value or unit based on user input
      if (name === 'value') {
        updatedValue = value;
      }
      if (name === 'unit') {
        updatedUnit = value;
      }

      const minutesValue = convertToMinutes(updatedValue, updatedUnit);

      setCompletionTime(`${updatedValue} ${updatedUnit}`);
      setCompletionTimeInMinutes(minutesValue);

      return {
        ...prevData,
        completionTimeInMinutes: minutesValue,
      };
    });
  };

  const handleInputChange = (event, index, field, optionIndex) => {
    const updatedQuestions = [...testData.questions];

    if (field === 'options') {
      updatedQuestions[index].options[optionIndex] = event.target.value;
    } else {
      updatedQuestions[index][field] = event.target.value;
    }

    setTestData({ ...testData, questions: updatedQuestions });
  };

  const handleTitleChange = (event) => {
    setTestData((prevData) => ({
      ...prevData,
      title: event.target.value,
    }));
  };

  const addQuestion = () => {
    const newQuestion = {
      questionText: '',
      options: ['', '', '', ''],
      correctAnswer: '',
    };

    setTestData((prevData) => ({
      ...prevData,
      questions: [...prevData.questions, newQuestion],
    }));
  };

  const removeQuestion = (index) => {
    const updatedQuestions = testData.questions.filter((_, i) => i !== index);
    setTestData((prevData) => ({
      ...prevData,
      questions: updatedQuestions,
    }));
  };

  const handleCorrectAnswerChange = (index, optionIndex) => {
    const updatedQuestions = [...testData.questions];
    updatedQuestions[index].correctAnswer =
      updatedQuestions[index].options[optionIndex];
    setTestData({ ...testData, questions: updatedQuestions });
  };

  useEffect(() => {
    const newErrors = [];

    if (!testData.title.trim()) {
      newErrors.push('Test title is required.');
    }

    testData.questions.forEach((question, index) => {
      if (!question.questionText.trim()) {
        newErrors.push(`Question ${index + 1} is incomplete.`);
      }
      if (
        testData.type === 'mcq' &&
        question.options.some((option) => !option.trim())
      ) {
        newErrors.push(`Question ${index + 1} has empty MCQ options.`);
      }
      if (question.correctAnswer === '') {
        newErrors.push(`Question ${index + 1} has no correct answer.`);
      }
      if (!question.marks) {
        newErrors.push(`Question ${index + 1} has no marks added`);
      }
    });

    setErrors(newErrors);
    setDisable(newErrors.length > 0);
  }, [testData, setDisable]);

  return (
    <div className="h-full w-full rounded-[20px] px-3 pt-7 md:px-8">
      <h4 className="pt-[5px] text-xl font-bold text-navy-700 dark:text-white">
        Test Information
      </h4>

      <InputField
        extra="mb-3"
        label="Test Title"
        placeholder="Enter Test Title"
        id="testTitle"
        type="text"
        onChange={handleTitleChange}
        value={testData?.title}
      />

      <div className="mb-3">
        <label className="ml-3  mr-1 text-sm font-bold text-navy-700 dark:text-white">
          Completion Time
        </label>
        <div className="mt-2 flex items-center gap-3">
          <input
            name="value"
            type="text"
            min="0.5"
            step="0.5"
            placeholder="Enter Time"
            className="rounded border border-gray-300 p-2 dark:!bg-navy-700"
            // onChange={handleCompletionTimeChange}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*\.?\d*$/.test(value)) {
                handleCompletionTimeChange(e);
              }
            }}
            value={completionTime.split(' ')[0] || ''}
          />

          <select
            name="unit"
            className="rounded border border-gray-300 p-2 dark:!bg-navy-700"
            onChange={handleCompletionTimeChange}
            value={completionTime.split(' ')[1] || 'hour'}
          >
            <option value="minute">Minutes</option>
            <option value="hour">Hours</option>
            <option value="day">Days</option>
          </select>
        </div>

        {/* Display converted time */}
        <p>Time in Minutes: {completionTimeInMinutes} minutes</p>
      </div>

      {testData.questions.map((question, index) => (
        <div key={index} className="mt-5">
          <h5 className="text-md font-semibold">Question {index + 1}</h5>

          <InputField
            extra="mb-3"
            label="Question Text"
            placeholder="Enter Question Text"
            type="text"
            onChange={(e) => handleInputChange(e, index, 'questionText')}
            value={question?.questionText}
          />

          {testData.type === 'mcq' && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {question.options.map((option, optionIndex) => (
                <div key={optionIndex} className="relative">
                  <input
                    type="text"
                    className={`w-full rounded border p-2 ${
                      question.correctAnswer === option
                        ? 'border-green-500 bg-green-100 dark:!bg-navy-700'
                        : 'border-gray-300 bg-green-100 dark:!bg-navy-700'
                    }`}
                    placeholder={`Option ${optionIndex + 1}`}
                    value={option}
                    onChange={(e) =>
                      handleInputChange(e, index, 'options', optionIndex)
                    }
                    onClick={() =>
                      handleCorrectAnswerChange(index, optionIndex)
                    }
                  />
                  {question.correctAnswer === option && (
                    <span className="absolute right-2 top-2 text-green-500">
                      ✓
                    </span>
                  )}
                </div>
              ))}
              <p className="col-span-full mt-1 pt-[5px]  text-sm  font-bold text-navy-700 dark:text-white">
                Note : Click on an option to mark it as the correct answer
              </p>
            </div>
          )}
          <InputField
            extra="mb-3"
            label="Marks"
            placeholder="Enter Marks"
            type="text"
            value={question?.marks}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*\.?\d*$/.test(value)) {
                handleInputChange(e, index, 'marks');
              }
            }}
            className="w-full rounded border p-2"
          />

          <button
            className="mt-2 text-red-500"
            onClick={() => removeQuestion(index)}
          >
            Remove Question
          </button>
        </div>
      ))}

      <button
        className="mt-5 rounded bg-brand-900 px-16 px-4 py-2 py-2 font-medium text-white text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
        onClick={addQuestion}
      >
        + Add Question
      </button>

      {errors.length > 0 && (
        <div className="mt-4 text-red-500">
          {errors.map((error, index) => (
            <p key={index}>{error}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddQuestion;
